// filepath: frontend/src/components/layout/Header.tsx
import { useState } from 'react';
import { Bell, Search, LogOut, WifiOff, Moon, Sun, LayoutDashboard, Menu } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useUIStore } from '@/store/uiStore';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { NotificationPanel } from './NotificationPanel';

export function Header() {
  const { user, logout } = useAuthStore();
  const { unreadCount } = useNotificationStore();
  const { data: actionPoints = [] } = useQuery<{ id: string }[]>({
    queryKey: ['action-points'],
    queryFn: () => api.get('/notifications/action-points').then(r => r.data),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  const { data: unreadData } = useQuery<{ unread_count: number }>({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.get('/notifications/unread-count').then(r => r.data),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  const apiUnread = unreadData?.unread_count ?? 0;
  const totalBadge = Math.max(unreadCount, apiUnread) + actionPoints.length;
  const { setCommandPaletteOpen, offlinePending, isOnline, darkMode, toggleDarkMode, toggleSidebar } = useUIStore();
  const [notifOpen, setNotifOpen] = useState(false);

  const handleLogout = async () => {
    await api.post('/auth/logout').catch(() => {});
    logout();
    window.location.href = '/login';
  };

  const { pathname } = useLocation();
  const isCeoOnModule = user?.role === 'ceo' && pathname.startsWith('/hub');
  const headerBg = 'rgba(14, 31, 52, 0.68)';
  const borderColor = 'rgba(255,255,255,0.12)';
  const textColor = 'white';

  return (
    <header
      className="h-14 flex-shrink-0 flex items-center justify-between px-5 gap-4 rounded-2xl"
      style={{
        background: headerBg,
        border: `1px solid ${borderColor}`,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: '0 14px 30px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
      role="banner"
    >
      <div className="flex items-center gap-3">
        {/* Burger — opens the navigation drawer */}
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          style={{ color: textColor, border: '1px solid rgba(255,255,255,0.08)' }}
          aria-label="Open navigation menu"
        >
          <Menu size={18} aria-hidden />
        </button>

        {/* CEO: back to control tower */}
        {isCeoOnModule ? (
          <Link
            to="/hub/control-tower"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors hover:bg-white/10"
            style={{ color: '#E47527', border: '1px solid rgba(228,117,39,0.25)', background: 'rgba(228,117,39,0.08)', whiteSpace: 'nowrap' }}
          >
            <LayoutDashboard size={14} />
            Control Tower
          </Link>
        ) : (
        /* Search trigger */
        <button
          onClick={() => setCommandPaletteOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors hover:bg-white/10"
          style={{ background: 'rgba(255,255,255,0.06)', color: '#838383', minWidth: 220, border: '1px solid rgba(255,255,255,0.08)' }}
          aria-label="Open command palette (⌘K)"
        >
          <Search size={14} aria-hidden />
          <span>Search…</span>
          <kbd className="ml-auto text-xs px-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>⌘K</kbd>
        </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Offline indicator */}
        {!isOnline && (
          <div className="flex items-center gap-1 text-xs px-2 py-1 rounded-full animate-pulse" style={{ background: '#ef444420', color: '#ef4444' }}>
            <WifiOff size={12} aria-hidden />
            Offline
          </div>
        )}

        {/* Offline queue indicator */}
        {offlinePending > 0 && (
          <div className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ background: '#E4752720', color: '#E47527' }}>
            <WifiOff size={12} aria-hidden />
            {offlinePending} pending
          </div>
        )}

        {/* Dark mode toggle */}
        <button
          onClick={toggleDarkMode}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          style={{ color: textColor, border: '1px solid rgba(255,255,255,0.08)' }}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {darkMode ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
        </button>

        {/* Notification bell */}
        <button
          onClick={() => setNotifOpen(!notifOpen)}
          className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
          style={{ color: textColor, border: '1px solid rgba(255,255,255,0.08)' }}
          aria-label={`Action Centre${totalBadge > 0 ? ` (${totalBadge})` : ''}`}
        >
          <Bell size={18} aria-hidden />
          {totalBadge > 0 && (
            <span
              className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-xs rounded-full font-bold"
              style={{ background: '#E47527', color: 'white', fontSize: 10 }}
              aria-hidden
            >
              {totalBadge > 9 ? '9+' : totalBadge}
            </span>
          )}
        </button>

        {/* User menu */}
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
              style={{ background: '#E47527', color: 'white' }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-xs font-medium truncate max-w-[120px]" style={{ color: textColor }}>
                {user?.name}
              </span>
              <span
                className="text-[10px] mt-0.5 px-1.5 py-0.5 rounded-full self-start"
                style={{
                  background: 'rgba(228,117,39,0.12)',
                  color: '#E47527',
                  border: '1px solid rgba(228,117,39,0.25)',
                }}
              >
                {user?.role?.replace('_', ' ') ?? ''}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            style={{ color: '#838383', border: '1px solid rgba(255,255,255,0.08)' }}
            aria-label="Log out"
          >
            <LogOut size={16} aria-hidden />
          </button>
        </div>
      </div>

      <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
    </header>
  );
}
